import type { AnatomyBinding, AnatomyVariant } from "./componentAnatomy";

// Row variants of the Menu x-ray, mined from designSystem/Menu/MenuItem.module.scss.
// The trailing indicator's `glyph` literal tells the schematic what to draw.

const itemFill = (value: `--${string}` | "transparent"): AnatomyBinding =>
    value === "transparent"
        ? {
              partId: "itemBackground",
              property: "background-color",
              literal: "transparent",
          }
        : {
              partId: "itemBackground",
              property: "background-color",
              token: value,
          };

const rowColor = (token: `--${string}`): readonly AnatomyBinding[] => [
    { partId: "itemLabel", property: "color", token },
    {
        partId: "glyph",
        property: "fill",
        token,
        note: "recipe-icon-color sets fill and stroke to currentColor, the row's color.",
    },
];

const indicatorColor = (
    token: `--${string}`,
    note?: string,
): AnatomyBinding => ({ partId: "indicator", property: "color", token, note });

const interactionBindings: AnatomyVariant["stateBindings"] = {
    hover: [itemFill("--bg-neutral-subtle-hover")],
    pressed: [itemFill("--bg-neutral-hover")],
    disabled: rowColor("--fg-disabled"),
};

const restingBindings = [itemFill("transparent"), ...rowColor("--fg-primary")];

/** A variant whose row ends in a trailing indicator, disabled with the row. */
const indicatorVariant = ({
    id,
    label,
    selector,
    indicatorBindings,
    disabledIndicatorBindings,
}: {
    id: string;
    label: string;
    selector: string;
    indicatorBindings: readonly AnatomyBinding[];
    disabledIndicatorBindings: readonly AnatomyBinding[];
}): AnatomyVariant => ({
    id,
    label,
    selector,
    restingBindings: [...restingBindings, ...indicatorBindings],
    stateBindings: {
        ...interactionBindings,
        disabled: [
            ...(interactionBindings.disabled ?? []),
            ...disabledIndicatorBindings,
        ],
    },
});

export const menuAnatomyVariants: readonly AnatomyVariant[] = [
    {
        id: "default",
        label: "Default",
        selector: "",
        restingBindings,
        stateBindings: interactionBindings,
    },
    {
        id: "destructive",
        label: "Destructive",
        selector: " .itemDestructive",
        restingBindings: [itemFill("transparent"), ...rowColor("--fg-error")],
        stateBindings: interactionBindings,
    },
    indicatorVariant({
        id: "submenu",
        label: "Submenu trigger",
        selector: " .itemTriangle",
        indicatorBindings: [
            {
                partId: "indicator",
                property: "glyph",
                literal: "triangle",
                note: "Figma Triangle=Yes: a TriangleIcon turned -90deg; the row keeps --bg-neutral-subtle-hover while [data-popup-open]. The nested popup takes --shadow-3.",
            },
            { partId: "indicator", property: "width", literal: "12px" },
            { partId: "indicator", property: "height", literal: "12px" },
            indicatorColor("--fg-tertiary", "Set on .itemTriangle."),
        ],
        disabledIndicatorBindings: [indicatorColor("--fg-disabled")],
    }),
    indicatorVariant({
        id: "toggle",
        label: "Toggle item",
        selector: " .itemSwitch",
        indicatorBindings: [
            {
                partId: "indicator",
                property: "glyph",
                literal: "switch",
                note: "SwitchIndicator (sm) painted from the row's checked state; its own tokens live in the Switch anatomy.",
            },
            { partId: "indicator", property: "width", literal: "28px" },
            { partId: "indicator", property: "height", literal: "16px" },
            {
                partId: "indicator",
                property: "margin-left",
                literal: "auto",
                note: "Through .itemTrailing: the switch sits on the row's right edge (Figma menu example 3967:1047).",
            },
        ],
        disabledIndicatorBindings: [],
    }),
    indicatorVariant({
        id: "radio",
        label: "Radio item",
        selector: " .itemCheck",
        indicatorBindings: [
            {
                partId: "indicator",
                property: "glyph",
                literal: "check",
                note: "Figma Check=Yes: Base UI RadioItemIndicator mounts the CheckIcon only on the selected row; .itemCheckSlot keeps the 16px slot on the others.",
            },
            { partId: "indicator", property: "width", literal: "16px" },
            { partId: "indicator", property: "height", literal: "16px" },
            indicatorColor(
                "--fg-primary",
                "The check takes the row colour through currentColor.",
            ),
        ],
        disabledIndicatorBindings: [indicatorColor("--fg-disabled")],
    }),
];
